/**
 * Stories & guides — ONE set of schemas for both sides: the write form's
 * react-hook-form resolver and the /api/stories handlers (`schema.parse`), so
 * client and server errors match. Limits mirror the CHECKs in
 * migrations/0022_stories.sql, all far below the 64KB request-body cap.
 */
import { z } from "zod";
import {
  MEMBER_COVER_IDS,
  STORY_CATEGORIES,
  STORY_SUBMISSION_STATUSES,
  THANKS_NOTES,
  currentStoryCategory,
} from "../types/stories.js";

export const STORY_TITLE_MIN = 8;
export const STORY_TITLE_MAX = 120;
export const STORY_BODY_MIN = 80;
/** ~1,400 words. */
export const STORY_BODY_MAX = 8000;
export const STORY_AUTHOR_BIO_MAX = 160;
export const STORY_REVIEW_NOTE_MAX = 1000;

/**
 * A topic. A renamed topic's old name ("Birth stories") is accepted and handed
 * on as the new one ("Family stories"), so an old link, bookmark or a tab
 * opened before the rename keeps working instead of answering 400. Anything
 * else is checked as is.
 */
function storyCategory(message?: string) {
  return z.preprocess(
    (value) => (typeof value === "string" ? currentStoryCategory(value) : value),
    message ? z.enum(STORY_CATEGORIES, { errorMap: () => ({ message }) }) : z.enum(STORY_CATEGORIES),
  );
}

export const submitStorySchema = z.object({
  title: z
    .string()
    .trim()
    .min(STORY_TITLE_MIN, "Give your story a title of at least a few words")
    .max(STORY_TITLE_MAX, "That title is a little long — try a shorter one"),
  category: storyCategory("Pick the topic that fits best"),
  body: z
    .string()
    .trim()
    .min(STORY_BODY_MIN, "A few more sentences, please — other parents want the details")
    .max(STORY_BODY_MAX, "That's longer than we can take in one story — try trimming it"),
  coverId: z.enum(MEMBER_COVER_IDS, {
    errorMap: () => ({ message: "Pick a cover" }),
  }),
  authorBio: z
    .string()
    .trim()
    .max(STORY_AUTHOR_BIO_MAX, "Keep it to one short line")
    .optional(),
});

export type SubmitStoryInput = z.infer<typeof submitStorySchema>;

/** Editing your own submission while it is still in review: the same fields. */
export const updateSubmissionSchema = submitStorySchema;
export type UpdateSubmissionInput = SubmitStoryInput;

/**
 * GET /api/stories query. An unknown category or a malformed cursor is a 400;
 * a renamed topic's old name lists the new topic.
 */
export const storyListQuerySchema = z.object({
  category: storyCategory().optional(),
  cursor: z.string().min(1).max(400).optional(),
  saved: z.enum(["1"]).optional(),
});

export type StoryListQueryInput = z.infer<typeof storyListQuerySchema>;

/** Story slugs: lowercase words joined by single hyphens. */
export const storySlugSchema = z
  .string()
  .min(1)
  .max(120)
  .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, "Not a story");

export const thankStorySchema = z.object({
  note: z.enum(THANKS_NOTES).nullable().optional(),
});

export const saveStorySchema = z.object({ saved: z.boolean() });

export const helpfulStorySchema = z.object({ helpful: z.boolean() });

/** Admin: the review queue filter (default: waiting for review). */
export const adminSubmissionListQuerySchema = z.object({
  status: z.enum(STORY_SUBMISSION_STATUSES).optional(),
});

/** Admin: publish — an optional note to the writer. */
export const publishStorySchema = z.object({
  note: z.string().trim().max(STORY_REVIEW_NOTE_MAX).optional(),
});

/** Admin: reject (or take down) — the writer always gets a reason. */
export const rejectStorySchema = z.object({
  note: z
    .string()
    .trim()
    .min(1, "Tell the writer why, kindly")
    .max(STORY_REVIEW_NOTE_MAX),
});
